/**
 * Command palette (Cmd/Ctrl+K on every surface): jump to any screen in the surface's menu by typing, with the screens
 * visited last on top. Customer workspaces can also run a full search from it. A combobox over a listbox: focus stays
 * in the field, arrows move the active option, Enter opens it, Escape or a click on the backdrop closes it.
 * Recent screens are remembered per surface on this device only (localStorage, best effort).
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useApp } from '../lib/app-context';
import { Icon } from './Icon';
import { entryFor } from './nav';
import type { ShellSurface } from './Shell';
import type { SurfaceNavItem } from './surface-nav';

const RECENT_MAX = 5;
const recentKey = (surface: ShellSurface) => `qm.recent.${surface}`;

function readRecent(surface: ShellSurface): string[] {
  try { const v = JSON.parse(localStorage.getItem(recentKey(surface)) ?? '[]') as unknown; return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []; } catch { return []; }
}

/** Remembers the menu entry of each page visited (most recent first). */
export function useRecentScreens(surface: ShellSurface, menu: readonly SurfaceNavItem[]): readonly string[] {
  const { pathname } = useLocation();
  const [recent, setRecent] = useState<string[]>(() => readRecent(surface));
  const to = entryFor(menu, pathname)?.to;
  useEffect(() => {
    if (!to) return;
    setRecent((prev) => {
      const next = [to, ...prev.filter((p) => p !== to)].slice(0, RECENT_MAX);
      try { localStorage.setItem(recentKey(surface), JSON.stringify(next)); } catch { /* storage blocked */ }
      return next;
    });
  }, [surface, to]);
  return recent;
}

/** Matches every word of the query against the label and its section, ignoring case and Arabic/Latin diacritics. */
export function matchMenu(items: readonly SurfaceNavItem[], query: string): SurfaceNavItem[] {
  const fold = (s: string) => s.normalize('NFKD').replace(/[̀-ًͯ-ٟ]/g, '').toLowerCase();
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (!words.length) return [...items];
  return items.filter((n) => { const hay = fold(`${n.label} ${n.group ?? ''} ${n.to}`); return words.every((w) => hay.includes(w)); });
}

export function CommandPalette({ surface, menu, open, onClose }: { surface: ShellSurface; menu: readonly SurfaceNavItem[]; open: boolean; onClose: () => void }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const recent = useRecentScreens(surface, menu);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    opener.current = typeof document !== 'undefined' ? document.activeElement : null;
    setQ(''); setActive(0);
    input.current?.focus();
    return () => { (opener.current as HTMLElement | null)?.focus?.(); };
  }, [open]);

  const options = useMemo(() => {
    const byTo = new Map(menu.map((n) => [n.to, n]));
    const recents = q.trim() ? [] : recent.map((to) => byTo.get(to)).filter((n): n is SurfaceNavItem => !!n);
    const rest = matchMenu(menu, q).filter((n) => !recents.includes(n));
    const rows: { item: SurfaceNavItem; section: string }[] = [
      ...recents.map((item) => ({ item, section: t('shell.palette.recent') })),
      ...rest.map((item) => ({ item, section: item.group ?? '' })),
    ];
    const search = surface === 'customer' && q.trim().length >= 2 ? { to: `/search?q=${encodeURIComponent(q.trim())}`, label: t('shell.palette.searchFor', { q: q.trim() }) } : null;
    return { rows, search };
  }, [menu, recent, q, surface, t]);
  const count = options.rows.length + (options.search ? 1 : 0);

  if (!open) return null;

  const go = (i: number) => {
    const row = options.rows[i];
    const to = row ? row.item.to : options.search?.to;
    if (!to) return;
    onClose();
    if (row?.item.document) window.location.assign(to); else navigate(to);
  };
  const onKey = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (count ? (a + 1) % count : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (count ? (a - 1 + count) % count : 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(active); }
    else if (e.key === 'Escape' || e.key === 'Tab') { e.preventDefault(); onClose(); }
  };
  const optId = (i: number) => `qm-palette-opt-${i}`;
  let lastSection: string | null = null;
  return (
    <div className="qm-palette" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="qm-palette__panel" role="dialog" aria-modal="true" aria-label={t('shell.palette.title')}>
        <div className="qm-palette__field">
          <Icon name="search" size={18} />
          <input ref={input} className="qm-palette__input" type="text" role="combobox" aria-expanded="true" aria-controls="qm-palette-list" aria-autocomplete="list"
            aria-activedescendant={count ? optId(Math.min(active, count - 1)) : undefined} aria-label={t('shell.palette.title')} placeholder={t('shell.palette.placeholder')}
            value={q} maxLength={100} onChange={(e) => { setQ(e.target.value); setActive(0); }} onKeyDown={onKey} />
          <kbd className="qm-shell__kbd" aria-hidden="true">Esc</kbd>
        </div>
        <ul id="qm-palette-list" className="qm-palette__list" role="listbox" aria-label={t('shell.palette.results')}>
          {options.rows.map(({ item, section }, i) => {
            const head = section && section !== lastSection ? section : null;
            lastSection = section;
            return (
              <li key={`${section}:${item.to}`} id={optId(i)} role="option" aria-selected={i === active} data-active={i === active ? 'true' : undefined}
                className="qm-palette__option" onMouseEnter={() => setActive(i)} onClick={() => go(i)}>
                {head ? <span className="qm-palette__section" aria-hidden="true">{head}</span> : null}
                <span className="qm-palette__row"><Icon name={item.icon} size={18} /><span>{item.label}</span></span>
              </li>
            );
          })}
          {options.search ? (
            <li id={optId(options.rows.length)} role="option" aria-selected={active === options.rows.length} data-active={active === options.rows.length ? 'true' : undefined}
              className="qm-palette__option" onMouseEnter={() => setActive(options.rows.length)} onClick={() => go(options.rows.length)}>
              <span className="qm-palette__row"><Icon name="search" size={18} /><span>{options.search.label}</span></span>
            </li>
          ) : null}
          {!count ? <li className="qm-palette__empty" role="presentation">{t('shell.palette.empty')}</li> : null}
        </ul>
      </div>
    </div>
  );
}
